/**
 * Camera exposure per altitude band and per lit body: the ease toward
 * `BAND_EXPOSURE`'s target, the per-body brightness scale metered against
 * `EXPOSURE_CALIBRATION_BODY`, and the altitude setter both ride through.
 */

import type { AtlasLive, AtlasState } from '../atlas';
import { columnTau550, columnTauRgb, deckAltitudeM, directBeamShare } from '../world/bodies.ts';
import { oldAir } from './atmosphere.ts';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { Band } from './camera';
import type { StageHandle } from '../world/stage';
import type { BodyRow } from '../world/types';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { NavigationHandle } from './navigation.ts';

/**
 * Camera exposure per altitude band, into `renderer.toneMappingExposure`.
 *
 * One number per band and not one for the Atlas, because the two ends of the
 * descent are not the same picture. `descent-dojo --colour` reads the drawn
 * frame's own levels at four of these bands, over the same ground with the sun
 * 31 degrees up and the clock held, and at exposure 1.0 they were
 *
 *   ORBIT     mean   9.7   p99  17   clipped 0.00%
 *   LOCAL     mean 107.4   p99 185   clipped 0.00%
 *   CLAIM     mean  90.0   p99 161   clipped 0.00%
 *   SURFACE   mean  57.7   p99 144   clipped 0.00%
 *
 * Nothing clips at any band, so the sky's own brightness argues for no
 * reduction at the surface bands. What the numbers do say is that ORBIT is an
 * order of magnitude darker than LOCAL for the SAME lit ground - the site is
 * at frame centre and its own sun is 31 degrees up at all four stops, one held
 * clock - so the three bands above the streaming edge take a bounded lift
 * (SYSTEM and ORBIT 1.25, REGIONAL 1.1) and the four below stay at 1.0.
 *
 * Bounded, because 1.25 does not close a 10x gap and is not trying to. Most of
 * that gap is not exposure at all: above the streaming edge the ground is the
 * orrery's own globe sphere and below it the pyramid's tiles, and the two are
 * lit by different materials. Closing it properly is that difference plus the
 * auto-exposure this table stands in for
 * (`docs/roadmap/05-atmosphere-and-light.md`, "Sun and exposure"). What the
 * lift is for here is the dark end of the ladder, and an edge for the ease to
 * be measurable across at all.
 *
 * LOCAL, CLAIM, PLOT and SURFACE carry the SAME value, and it is the 1.0 the
 * whole Atlas used to run at. That used to be a constraint rather than a
 * choice, because the `dtmPlot.ts` materials wrote gl_FragColor with no
 * tone-mapping chunk on it and so did not respond to this number while the
 * tiles they replace at the PLOT->SURFACE handover did. They carry
 * `<tonemapping_fragment>` and `<colorspace_fragment>` now, so all four are
 * free to move together; the step a difference between them would open across
 * the handover is what `surface-clamp --edge` reads.
 */
export const BAND_EXPOSURE: Readonly<Record<Band, number>> = {
  SYSTEM: 1.25,
  ORBIT: 1.25,
  REGIONAL: 1.1,
  LOCAL: 1.0,
  CLAIM: 1.0,
  PLOT: 1.0,
  SURFACE: 1.0,
};

/** The body the exposure table above and `SKY_EXPOSURE` were both picked on. */
export const EXPOSURE_CALIBRATION_BODY = 'mars';

/**
 * The ground's own radiance under a deck, W/m2/sr: the flux the deck lets
 * through off the ground's albedo, or null where the row carries no deck floor.
 * Venus's is 3.2 against its cloud top's 637, which is what the camera is
 * pointed at once it stands under the deck.
 */
function deckFloorLitRadiance(row: BodyRow | undefined): number | null {
  const flux = row?.solar_flux_mean_W_m2 ?? null;
  if (!row || flux === null || !(flux > 0) || row.atmosphere.kind !== 'data') return null;
  const f = row.atmosphere.deck_floor_flux;
  if (!f || deckAltitudeM(row) === null) return null;
  return (row.atmosphere.ground_albedo * ((f[0] + f[1] + f[2]) / 3) * flux) / Math.PI;
}

/**
 * The lowest sun, as its sine, the camera under a deck opens up for: about 6
 * degrees, below which the ground goes dark into the night as it would anyway.
 */
const DECK_METER_MU_MIN = 0.1;

/**
 * How far the camera under a deck opens past the floor's own scale, which
 * shows the deck's underside under an overhead sun: it keeps the frame's
 * log-average there instead, half that underside and half the ground it lights
 * at the row's albedo, falling with the sun's height as both do (`atmoDeck`).
 * Venus's is 2.6 at an overhead sun and 15 at a 10 degree one.
 */
function deckFloorOpening(row: BodyRow, sunMu: number): number {
  const albedo = row.atmosphere.kind === 'data' ? row.atmosphere.ground_albedo : 1;
  return 1 / (Math.sqrt(Math.max(albedo, 1e-3)) * Math.max(sunMu, DECK_METER_MU_MIN));
}

/** How far through the deck the camera has come: 0 at its top and above, 1 at its base and below. */
function deckDepth(row: BodyRow, altitudeM: number): number {
  const base = deckAltitudeM(row);
  if (base === null || row.atmosphere.kind !== 'data') return 0;
  const top = Math.max(row.atmosphere.deck_top_m ?? base, base);
  if (top <= base) return altitudeM <= base ? 1 : 0;
  return Math.min(1, Math.max(0, (top - altitudeM) / (top - base)));
}

/**
 * The ground's own radiance under a column deeper than `ref`'s with no deck
 * over it, W/m2/sr, or null where the column is no deeper or a deck caps it.
 * `directBeamShare` takes the excess depth off the beam and `syncGroundSky`
 * hands the ground all of it back as diffuse light, so this is the ground's
 * albedo under the mean flux: Titan's 0.33 against Mars's 47.
 */
function hazeFloorLitRadiance(row: BodyRow, ref: BodyRow | undefined): number | null {
  const flux = row.solar_flux_mean_W_m2 ?? null;
  const air = row.atmosphere;
  if (flux === null || !(flux > 0) || air.kind !== 'data' || deckAltitudeM(row) !== null) return null;
  if (ref?.atmosphere.kind !== 'data' || !(directBeamShare(air, ref.atmosphere) < 1)) return null;
  return (air.ground_albedo * flux) / Math.PI;
}

/**
 * How far into the excess column the camera has come: 0 where what is left
 * over it is no deeper than `ref`'s whole column, 1 at the ground. Titan's
 * reaches 0 about 180 km up, three of its haze's scale heights.
 */
function hazeDepth(row: BodyRow, ref: BodyRow, altitudeM: number): number {
  if (row.atmosphere.kind !== 'data' || ref.atmosphere.kind !== 'data') return 0;
  const refTau = columnTau550(ref.atmosphere);
  const excess = columnTau550(row.atmosphere) - refTau;
  if (!(excess > 0)) return 0;
  return Math.min(1, Math.max(0, (columnTau550(row.atmosphere, altitudeM) - refTau) / excess));
}

/**
 * The eye's white balance at the ground under the excess column, for what the
 * air scatters: (Y(T) / T)^D per channel, T what the excess over `ref`'s
 * column leaves of the beam, so the gain keeps the light's luminance and takes
 * out its colour as far as the eye adapts. D is CIECAM02's degree of
 * adaptation at the ground's luminance `litRadiance` * Y(T), which is 0.82 in
 * Titan's dim light: full adaptation turned its sky lavender grey, since the
 * model's sky is barely redder than the beam. The ground's own light is
 * already taken over the zenith column (`groundLightTint` in
 * src/atlas/farField.ts). Null where the column is no deeper than `ref`'s.
 */
function hazeWhiteGain(row: BodyRow, ref: BodyRow, litRadiance: number): [number, number, number] | null {
  if (row.atmosphere.kind !== 'data' || ref.atmosphere.kind !== 'data') return null;
  const here = columnTauRgb(row.atmosphere);
  const there = columnTauRgb(ref.atmosphere);
  const t = here.map((v, c) => Math.exp(-Math.max(0, v - there[c]!)));
  const y = 0.2126 * t[0]! + 0.7152 * t[1]! + 0.0722 * t[2]!;
  if (!(y > 0)) return null;
  const adaptingCdM2 = litRadiance * y * SUNLIGHT_LM_PER_W;
  const d = 1 - Math.exp((-adaptingCdM2 - 42) / 92) / 3.6;
  return [(y / t[0]!) ** d, (y / t[1]!) ** d, (y / t[2]!) ** d];
}

/** Sunlight's luminous efficacy, lm/W, for the adapting luminance above. */
const SUNLIGHT_LM_PER_W = 93;

/**
 * A body's own sub-solar radiance, W/m2/sr: what the camera is pointed at.
 *
 * Its mean flux and not the live seasonal one, so this is a constant per body -
 * a factor that moved with Ls would put a slow exposure ramp on a year sweep
 * that no camera setting explains. The albedo is the deck's where the body has
 * one, because from outside a deck body the deck IS the surface.
 */
function bodyLitRadiance(row: BodyRow | undefined): number | null {
  const flux = row?.solar_flux_mean_W_m2 ?? null;
  if (!row || flux === null || !Number.isFinite(flux) || flux <= 0) return null;
  if (row.atmosphere.kind !== 'data') return null;
  const a = row.atmosphere.deck_albedo;
  const albedo = a ? (a[0] + a[1] + a[2]) / 3 : row.atmosphere.ground_albedo;
  return (albedo * flux) / Math.PI;
}

/**
 * The most the exposure may move in one drawn frame.
 *
 * A band change is a step in the table and a step in exposure is a step in
 * every lit pixel at once, which is the definition of the pop the descent
 * recorder counts. Per FRAME and not per second, so a frame that takes 300 ms
 * - which the PLOT->SURFACE entry does - cannot cut on the strength of its own
 * dt. The largest step between adjacent bands is 0.15, which crosses in 15
 * frames - 250 ms at cadence; the table's full 0.25 takes 0.4 s.
 */
export const EXPOSURE_EASE_PER_FRAME = 0.01;

export interface ExposureDeps {
  readonly focusRig: FocusRigHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly navigation: NavigationHandle;
  readonly live: AtlasLive;
  readonly stage: StageHandle;
  readonly state: AtlasState;
  readonly atmosphereView: AtmosphereViewHandle;
}

export interface ExposureHandle {
  readonly exposureDebug: { forceScale: number | null; easeAcrossBody: boolean; meterAtSystem: boolean };
  readonly state: { readonly exposure: number };
  bodyExposureScale(id: string | null): number;
  bodyScaleAppliesHere(): boolean;
  updateExposure(): boolean;
  setAltitude(metres: number): void;
}

export function createExposure(deps: ExposureDeps): ExposureHandle {
  const { stage, state } = deps;
  const { band, focusCam } = deps.focusRig;
  const { rowById } = deps.bodyReaders;

  /**
   * Walk the exposure toward the band's own entry in `BAND_EXPOSURE`.
   *
   * Returns whether it is still moving, which is what keeps the render-on-demand
   * loop drawing while it does - an ease nothing redraws is a cut with extra
   * steps.
   */
  /**
   * How far the camera stops DOWN on a body brighter than the calibration one.
   *
   * The scene is lit in absolute irradiance, so a body's own level is its
   * albedo times its flux, and one exposure table cannot hold both ends of
   * that: Venus's cloud top at 0.77 under 2,601 W/m2 is 637 W/m2/sr against
   * Mars's ground at 46.6, thirteen times over, and the ORBIT band drew 88
   * percent of the disc inside three grey levels of its own peak - a flat white
   * ball with no limb in it.
   *
   * DOWN only, and never up. Opening the table on a dim world moves every
   * reading recorded out there - `descent-dojo --rings` reads Saturn's B ring
   * in grey levels at 14.8 W/m2 - and that is the auto-exposure package's
   * business (docs/roadmap/05-atmosphere-and-light.md, "Sun and exposure"), not
   * this one's. A row with no `kind: 'data'` atmosphere carries no albedo to
   * meter and stays at 1, which is every airless body and every giant, so what
   * this actually moves today is Venus and nothing else.
   */
  /**
   * `descent-dojo --deck --inject flat-exposure`: the clip bar's own control.
   * Overrides the scale below outright, so a build that stopped clipping
   * because the per-body exposure now meters Venus's cloud top down can be
   * put back where it clipped and prove the bar still fires.
   */
  const exposureDebug: { forceScale: number | null; easeAcrossBody: boolean; meterAtSystem: boolean } = {
    forceScale: null, easeAcrossBody: false, meterAtSystem: false,
  };
  /**
   * Where the eased exposure is right now. The band's own target is
   * `BAND_EXPOSURE[band()]`; this is what the stage applies, and its band
   * part walks toward that target at `EXPOSURE_EASE_PER_FRAME`.
   */
  let exposure = BAND_EXPOSURE.SYSTEM;
  /** The eased part of `exposure`: the band's entry times the top scale, before any opening under a deck. */
  let easedLevel = BAND_EXPOSURE.SYSTEM;
  // Fixed per focused body, so two registry scans a frame is two too many -
  // memoised on the id itself, since `state.bodyId` is set from several call
  // sites and matching the id is safer than hooking every one of them. Cached
  // only once `rowById` actually finds the row: the registry loads once, off
  // the network, and a call landing before it has is not a body whose own
  // scale is 1 - it is `bodyRows` still empty, and caching that would freeze
  // every later call on the wrong answer.
  let exposureScaleMemoId: string | null | undefined;
  let exposureScaleMemoValue = 1;
  let exposureFloorMemo: number | null = null;
  let exposureHazeMemo: number | null = null;
  let exposureWhiteMemo: [number, number, number] | null = null;
  let exposureRowMemo: BodyRow | undefined;
  let exposureRefMemo: BodyRow | undefined;
  /** The scale metered off the body as seen from outside: its deck's top where it has one. */
  function bodyTopScale(id: string | null): number {
    if (id === exposureScaleMemoId) return exposureScaleMemoValue;
    const row = rowById(id);
    if (row === undefined) return 1;
    const refRow = rowById(EXPOSURE_CALIBRATION_BODY);
    const ref = bodyLitRadiance(refRow);
    const meter = (here: number | null): number =>
      here === null || ref === null || !(here > 0) ? 1 : Math.min(1, ref / here);
    exposureScaleMemoValue = meter(bodyLitRadiance(row));
    const floor = deckFloorLitRadiance(row);
    exposureFloorMemo = floor === null ? null : meter(floor);
    const haze = hazeFloorLitRadiance(row, refRow);
    exposureHazeMemo = haze === null || ref === null || !(haze > 0) ? null : ref / haze;
    exposureWhiteMemo = exposureHazeMemo !== null && haze !== null && refRow ? hazeWhiteGain(row, refRow, haze) : null;
    exposureRowMemo = row;
    exposureRefMemo = refRow;
    exposureScaleMemoId = id;
    return exposureScaleMemoValue;
  }
  /** The sun's height over the eye as the mounted air sees it, its sine; 1 where the focused body's air is not mounted. */
  function sunMuHere(): number {
    const view = deps.atmosphereView;
    if (!view.atmo || view.atmoBodyId !== state.bodyId) return 1;
    return Math.min(1, (view.atmo.uniforms['uAtmoSunDir']!.value as { y: number }).y);
  }
  /**
   * The top scale, eased in log over the deck's own thickness to the scale
   * metered off the ground under it, as the focused camera comes down through
   * it, opened by `deckFloorOpening`: Venus's 0.073 at 70 km to 2.6 at 48 km
   * under an overhead sun. A deckless column deeper than the calibration
   * body's eases the same way over `hazeDepth` to the scale metered off its
   * ground, which opens UP: Titan's 1 above about 180 km to 141 at the ground.
   * `?oldair=1` keeps the top scale all the way down.
   */
  function bodyExposureScale(id: string | null): number {
    if (exposureDebug.forceScale !== null) return exposureDebug.forceScale;
    const top = bodyTopScale(id);
    const floor = exposureFloorMemo;
    if (oldAir() || id !== state.bodyId || !exposureRowMemo) return top;
    const haze = exposureHazeMemo;
    if (haze !== null && exposureRefMemo) {
      const depth = hazeDepth(exposureRowMemo, exposureRefMemo, deps.focusRig.altitude());
      return depth > 0 ? top * Math.pow(haze / top, depth) : top;
    }
    if (floor === null) return top;
    const depth = deckDepth(exposureRowMemo, deps.focusRig.altitude());
    if (!(depth > 0)) return top;
    const lit = floor * deckFloorOpening(exposureRowMemo, sunMuHere());
    return top * Math.pow(lit / top, depth);
  }

  /**
   * Whether the per-body scale applies at the band the camera is in right now.
   *
   * SYSTEM draws the orrery, every body at once, so metering it for whichever
   * one is focused tinted every other body's ink dark on a pick - the
   * scale belongs only to bands where the focused globe is the subject.
   * `meterAtSystem` is `--inject focus-meter-system`, restoring the old table.
   */
  function bodyScaleAppliesHere(): boolean {
    return exposureDebug.meterAtSystem || band() !== 'SYSTEM';
  }

  /**
   * The scale the exposure was last metered with, so a step in it is visible.
   *
   * The ease above is for a BAND change, which is continuous: the ladder is
   * between two rungs of the same table on one body. The per-body scale is not
   * continuous, and neither is what it meters - `updateAtmoTerrainFrame` writes
   * `uAtmoGroundScale` in full on the frame a body mounts, 8.13 on Venus
   * against 1.83 on Mars - so an exposure walking to the new scale at the band
   * rate draws the arriving body metered for the one that was focused before
   * it. Venus's own gap is 1.1585, which is 116 frames of it.
   *
   * `stepped` tracks the RAW scale rather than the SYSTEM-gated one below, so
   * that gate flipping at a band edge rides the ordinary band-change ease
   * above instead of taking this step's cut - the raw scale itself has not
   * moved, only whether it applies here. It tracks the body's top scale and
   * not the deck-depth blend, which is not eased at all.
   */
  /** `hazeWhiteGain` to the power of `hazeDepth` on the mounted air, so 1 wherever the depth is 0. */
  function syncWhite(): void {
    const view = deps.atmosphereView;
    const white = view.atmo?.uniforms['uAtmoWhite']?.value as { set(x: number, y: number, z: number): void } | undefined;
    if (!white) return;
    const gain = exposureWhiteMemo;
    const depth = gain && !oldAir() && view.atmoBodyId === state.bodyId && exposureRowMemo && exposureRefMemo
      ? hazeDepth(exposureRowMemo, exposureRefMemo, deps.focusRig.altitude()) : 0;
    if (!gain || !(depth > 0)) white.set(1, 1, 1);
    else white.set(gain[0] ** depth, gain[1] ** depth, gain[2] ** depth);
  }

  let exposureScaleApplied = 1;
  function updateExposure(): boolean {
    const rawScale = bodyExposureScale(state.bodyId);
    syncWhite();
    const topScale = exposureDebug.forceScale ?? bodyTopScale(state.bodyId);
    const stepped = topScale !== exposureScaleApplied;
    exposureScaleApplied = topScale;
    const applies = bodyScaleAppliesHere();
    // The opening under a deck or haze follows the camera's height and the sun, so it applies as it stands:
    // eased at the band rate, a jump back to orbit kept the camera metered for the ground for a minute.
    const opening = applies && topScale > 0 ? rawScale / topScale : 1;
    const target = BAND_EXPOSURE[band()] * (applies ? topScale : 1);
    const gap = target - easedLevel;
    if (stepped && !exposureDebug.easeAcrossBody) easedLevel = target;
    else if (gap !== 0) {
      easedLevel = Math.abs(gap) <= EXPOSURE_EASE_PER_FRAME ? target : easedLevel + Math.sign(gap) * EXPOSURE_EASE_PER_FRAME;
    }
    const next = easedLevel * opening;
    if (next === exposure) return false;
    exposure = next;
    stage.setToneMappingExposure(exposure);
    deps.live.needsRender = true;
    return easedLevel !== target;
  }

  function setAltitude(metres: number): void {
    focusCam.setAltitude(metres);
    deps.live.cameraDirty = true;
    deps.navigation.syncTierToAltitude();
  }

  return {
    exposureDebug, bodyExposureScale, bodyScaleAppliesHere, updateExposure, setAltitude,
    state: { get exposure() { return exposure; } },
  };
}
